import { SubFormType } from '@ikegps/core-api';
import { type FormModelField } from './form.field';
import type { FormModel } from './form';
import { canMigrateFieldType } from './form.field.type.conversion';
import { FormModelFieldSubForm } from './subform/form.subform';
import { IPFToPLAFieldMap, PLAToIPFFieldMap } from '../pole/integrations/fieldMapping';

export interface FormFieldAutoMatch {
	sourceField: FormModelField;
	targetField: FormModelField | null;
}

type Crosswalk = Record<string, string | undefined>;

interface AutoMatchContext {
	crosswalk: Crosswalk | null;
	claimedTargetFieldIds: Set<string>;
}

/** Subform types that declare a fixed set of named field roles (e.g. a Span's vector field). */
const TYPED_SUBFORM_TYPES: SubFormType[] = [SubFormType.SPAN, SubFormType.ANCHOR, SubFormType.EQUIPMENT];

function isDefinedRoleEntry(entry: [string, string | undefined]): entry is [string, string] {
	return entry[1] != null;
}

/** The role -> field id pairs a typed subform actually has set, e.g. `leadLengthFieldId` on an Anchor. */
function typedRoleEntries(subform: FormModelFieldSubForm): [string, string][] {
	switch (subform.subFormType) {
		case SubFormType.SPAN: {
			const state = subform.subFormSpanTypeState;
			return state == null ? [] : Object.entries(state).filter(isDefinedRoleEntry);
		}
		case SubFormType.ANCHOR: {
			const state = subform.subFormAnchorState;
			return state == null ? [] : Object.entries(state).filter(isDefinedRoleEntry);
		}
		case SubFormType.EQUIPMENT: {
			const state = subform.subFormEquipmentState;
			return state == null ? [] : Object.entries(state).filter(isDefinedRoleEntry);
		}
		default:
			return [];
	}
}

function sameTypedSubforms(
	sourceScope: FormModelFieldSubForm,
	targetScope: FormModelFieldSubForm | null
): targetScope is FormModelFieldSubForm {
	return (
		targetScope != null &&
		sourceScope.subFormType != null &&
		TYPED_SUBFORM_TYPES.includes(sourceScope.subFormType) &&
		sourceScope.subFormType === targetScope.subFormType
	);
}

/**
 * Matches a subform's own typed-role fields (e.g. a Span's vector field, an Anchor's owner field)
 * directly against the same role on `targetSubform`, independent of id or display name — two
 * independently-built subforms of the same type share neither. Only a role set on both sides matches.
 * Returns the `fieldId` of every source field this claims, so the caller can skip them in its own
 * id/crosswalk/name matching below.
 */
function matchTypedSubformFields(
	sourceSubform: FormModelFieldSubForm,
	targetSubform: FormModelFieldSubForm,
	context: AutoMatchContext,
	matches: FormFieldAutoMatch[]
): Set<string> {
	const targetRoles = new Map(typedRoleEntries(targetSubform));
	const claimedSourceFieldIds = new Set<string>();

	for (const [role, sourceFieldId] of typedRoleEntries(sourceSubform)) {
		const targetFieldId = targetRoles.get(role);
		if (targetFieldId == null) {
			continue;
		}

		const sourceField = sourceSubform.fields.find((field) => field.id === sourceFieldId);
		const targetField = targetSubform.fields.find((field) => field.id === targetFieldId);

		if (
			sourceField == null ||
			targetField == null ||
			context.claimedTargetFieldIds.has(targetField.fieldId) ||
			!canMigrateFieldType(sourceField.type, targetField.type)
		) {
			continue;
		}

		context.claimedTargetFieldIds.add(targetField.fieldId);
		claimedSourceFieldIds.add(sourceField.fieldId);
		matches.push({ sourceField, targetField });
	}

	return claimedSourceFieldIds;
}

/** The curated PLA <-> IPF crosswalk applies only when exactly one side of the migration is an IPF form. */
function getCrosswalk(sourceForm: FormModel, targetForm: FormModel): Crosswalk | null {
	const sourceIsIPF = Boolean(sourceForm.metadata.getIkePoleForeman());
	const targetIsIPF = Boolean(targetForm.metadata.getIkePoleForeman());

	if (sourceIsIPF === targetIsIPF) {
		return null;
	}

	return sourceIsIPF ? IPFToPLAFieldMap : PLAToIPFFieldMap;
}

/**
 * Picks a target for one source field from the candidates it may reach, in four tiers:
 *
 * 1. Field id. Same-family migrations (a form version bump, a copied form) keep field ids stable,
 *    and collection data is stored keyed by field id, so these carry over as-is.
 * 2. Subform type, for a source subform only. A subform tagged Span/Anchor/Equipment matches the one
 *    candidate subform sharing that type, even when their ids and names differ entirely — two
 *    independently-built forms can each have their own "Span" subform under unrelated names.
 * 3. The curated PLA <-> IPF crosswalk. It covers the standard field vocabulary only
 *    (`poleID` <-> `ipf_id` etc.); custom fields fall through to tier 4.
 * 4. Display name, preferring an identical type over one the data can merely be converted onto.
 */
function matchField(
	sourceField: FormModelField,
	candidates: FormModelField[],
	context: AutoMatchContext
): FormModelField | null {
	const available = candidates.filter((candidate) => !context.claimedTargetFieldIds.has(candidate.fieldId));

	const idMatch = available.find((candidate) => candidate.id === sourceField.id);
	if (idMatch != null && canMigrateFieldType(sourceField.type, idMatch.type)) {
		return idMatch;
	}

	if (FormModelFieldSubForm.isSubForm(sourceField) && sourceField.subFormType != null) {
		const subFormType = sourceField.subFormType;

		if (TYPED_SUBFORM_TYPES.includes(subFormType)) {
			const typeMatches = available.filter(
				(candidate): candidate is FormModelFieldSubForm =>
					FormModelFieldSubForm.isSubForm(candidate) && candidate.subFormType === subFormType
			);

			if (typeMatches.length === 1) {
				return typeMatches[0];
			}
		}
	}

	if (context.crosswalk != null) {
		const crosswalkTargetId = context.crosswalk[sourceField.id];
		const crosswalkMatch =
			crosswalkTargetId != null ? available.find((candidate) => candidate.id === crosswalkTargetId) : undefined;

		if (crosswalkMatch != null) {
			return crosswalkMatch;
		}
	}

	const named = available.filter((candidate) => candidate.displayName === sourceField.displayName);
	const sameType = named.filter((candidate) => candidate.type === sourceField.type);
	const byName =
		sameType.length > 0 ? sameType : named.filter((candidate) => canMigrateFieldType(sourceField.type, candidate.type));

	return byName.length === 1 ? byName[0]! : null;
}

/**
 * Walks a source subform against the target it is mapped to. Mapping is one-directional: a field's
 * candidates are every field at or beneath whatever its parent matched (that whole branch's subtree,
 * not just its immediate children), so data can move down into deeper nesting but never back up to a
 * shallower level. A group that matched nothing leaves its whole subtree unmatched.
 *
 * When the source and target scopes are subforms of the same declared type (e.g. both Span), their
 * typed-role fields are matched directly by role first — see `matchTypedSubformFields` — since two
 * independently-built subforms of the same type share neither ids nor names. Everything else in the
 * scope still falls through to the ordinary per-field tiers below.
 */
function matchScope(
	sourceScope: FormModelFieldSubForm,
	targetScope: FormModelFieldSubForm | null,
	context: AutoMatchContext,
	matches: FormFieldAutoMatch[]
): void {
	const candidates = targetScope == null ? [] : targetScope.getFieldsRecursive;
	const roleMatchedSourceFieldIds = sameTypedSubforms(sourceScope, targetScope)
		? matchTypedSubformFields(sourceScope, targetScope, context, matches)
		: new Set<string>();

	for (const sourceField of sourceScope.fields) {
		if (roleMatchedSourceFieldIds.has(sourceField.fieldId)) {
			continue;
		}

		const targetField = candidates.length === 0 ? null : matchField(sourceField, candidates, context);

		if (targetField != null) {
			context.claimedTargetFieldIds.add(targetField.fieldId);
		}

		matches.push({ sourceField, targetField });

		if (FormModelFieldSubForm.isSubForm(sourceField)) {
			const nestedTarget = targetField != null && FormModelFieldSubForm.isSubForm(targetField) ? targetField : null;
			matchScope(sourceField, nestedTarget, context, matches);
		}
	}
}

/** Proposes a target field for every field of the source form. */
export function autoMatchFormFields(sourceForm: FormModel, targetForm: FormModel): FormFieldAutoMatch[] {
	const matches: FormFieldAutoMatch[] = [];

	matchScope(
		sourceForm,
		targetForm,
		{ crosswalk: getCrosswalk(sourceForm, targetForm), claimedTargetFieldIds: new Set() },
		matches
	);

	return matches;
}

/**
 * Proposes a target for every field inside one source subform, against the subform it has just been
 * mapped to. Used when a group's own mapping changes, so its contents are re-matched against the new
 * group rather than left unmatched.
 *
 * `claimedTargetFieldIds` are target fields already spoken for elsewhere in the form; targets claimed
 * by the returned matches are added to it.
 */
export function autoMatchSubformFields(
	sourceSubform: FormModelFieldSubForm,
	targetSubform: FormModelFieldSubForm | null,
	sourceForm: FormModel,
	targetForm: FormModel,
	claimedTargetFieldIds: Set<string> = new Set()
): FormFieldAutoMatch[] {
	const matches: FormFieldAutoMatch[] = [];

	matchScope(
		sourceSubform,
		targetSubform,
		{ crosswalk: getCrosswalk(sourceForm, targetForm), claimedTargetFieldIds },
		matches
	);

	return matches;
}
